import type { CarouselBgCandProgress, CarouselLlmSegment } from "@/lib/api";

const DEFAULT_MS = 10_000;

function elapsedMs(iso?: string, now = Date.now()): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, now - t);
}

export function estimateBgCandProgress(
  progress: CarouselBgCandProgress | null | undefined,
  now = Date.now(),
): {
  percent: number;
  eta_sec: number;
  elapsed_sec: number;
  ms_per_variant: number;
  segments: CarouselLlmSegment[];
  expected_sec: number;
} {
  const total = Math.max(1, Number(progress?.total || 3));
  const done = Math.max(0, Math.min(Number(progress?.done || 0), total));
  const phase = progress?.phase || "waiting";
  const expected = Math.max(3500, Number(progress?.expected_ms_per_variant || DEFAULT_MS));
  const elapsed = elapsedMs(progress?.started_at, now);
  const current = elapsedMs(progress?.variant_started_at || progress?.started_at, now);
  const elapsedSec = Math.max(0, Math.round(elapsed / 1000));
  const expectedSec = Math.max(1, Math.round((Number(progress?.job_expected_ms) || expected * total) / 1000));
  const jobMs = expected * total;
  const timePct = jobMs ? Math.min(92, Math.max(1, (100 * elapsed) / jobMs)) : 1;
  const timeEta = Math.max(0, (jobMs - elapsed) / 1000);

  if (phase === "done" || done >= total) {
    return {
      percent: 100,
      eta_sec: 0,
      elapsed_sec: elapsedSec,
      ms_per_variant: expected,
      expected_sec: expectedSec,
      segments: buildSegments(done, total, total, expected, 1),
    };
  }
  if (phase === "error" || phase === "failed") {
    return {
      percent: Math.max(1, Number(progress?.percent) || Math.round(timePct)),
      eta_sec: 0,
      elapsed_sec: elapsedSec,
      ms_per_variant: expected,
      expected_sec: expectedSec,
      segments: (progress?.segments || []).length
        ? progress!.segments!
        : buildSegments(done, total, Number(progress?.current || done + 1), expected, 0.4),
    };
  }

  let msPer = expected;
  const completedMs = Math.max(0, elapsed - current);
  if (done >= 1 && completedMs >= 400) {
    const live = completedMs / done;
    msPer = done < 2 ? (live + expected) / 2 : live;
  }
  msPer = Math.max(2500, msPer);
  const frac = done < total ? Math.min(0.92, current / msPer) : 0;
  const effective = done + frac;
  const stepPct = Math.min(99, Math.max(1, (100 * effective) / total));
  const stepEta = ((total - effective) * msPer) / 1000;
  const percent = Math.min(99, Math.max(timePct, stepPct, Number(progress?.percent) || 0));
  const eta = done >= 1 ? Math.min(timeEta, stepEta) : timeEta;
  const currentIdx = Math.min(total, Math.max(1, Number(progress?.current || done + 1)));
  return {
    percent: Math.max(1, Math.round(percent)),
    eta_sec: Math.max(0, Math.round(eta)),
    elapsed_sec: elapsedSec,
    ms_per_variant: Math.round(msPer),
    expected_sec: expectedSec,
    segments: buildSegments(done, total, currentIdx, msPer, frac),
  };
}

function buildSegments(
  done: number,
  total: number,
  current: number,
  weightMs: number,
  currentFrac: number,
): CarouselLlmSegment[] {
  const rows: CarouselLlmSegment[] = [];
  for (let k = 1; k <= total; k += 1) {
    let fraction = 0;
    if (k <= done) fraction = 1;
    else if (k === current) fraction = Math.max(0, Math.min(0.99, currentFrac));
    rows.push({
      id: `v${k}`,
      label: `Wariant ${k}`,
      weight_ms: weightMs,
      fraction: Number(fraction.toFixed(4)),
    });
  }
  return rows;
}
